
import { defineComponent, PropType } from 'vue';
import type { User } from '@white-label-types/parking-shared';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { FEATURE_FLAGS } from '@white-label-configuration/constants';

type ReorderedUserData = {
  first_name: string;
  last_name: string;
  phone: string | null;
  zipcode: string | null;
  inbound_flight: string | null;
  outbound_flight: string | null;
};

// Components
import ToggleContent from '../toggle-content/toggle-content.vue';

export default defineComponent({
  name: 'BTicketPersonalDetails',

  components: {
    ToggleContent,
  },

  props: {
    user: {
      type: Object as PropType<User>,
      required: true,
    },
  },

  computed: {
    reorderedUserData(): ReorderedUserData {
      return {
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        phone: this.user.phone.formatted_number,
        zipcode: this.user.zipcode,
        inbound_flight: this.showFlightLookup
          ? this.inboundDetailedFlight
          : this.user.inbound_flight,
        outbound_flight: this.showFlightLookup
          ? this.outboundDetailedFlight
          : this.user.outbound_flight,
      };
    },

    visibleUserDataEntries(): [string, string | null][] {
      const entries = Object.entries(this.reorderedUserData);
      return entries.filter(([label, value]) => this.isVisible(label, value));
    },

    licencePlateTranslation(): string {
      return this.$te(`receipt.ticketLabels.license_plate`)
        ? `receipt.ticketLabels.license_plate`
        : `manageBooking.ticketLabels.license_plate`;
    },
    vehicleMakeTranslation(): string {
      return this.$te(`receipt.ticketLabels.vehicle_make`)
        ? `receipt.ticketLabels.vehicle_make`
        : `manageBooking.ticketLabels.vehicle_make`;
    },
    vehicleModelTranslation(): string {
      return this.$te(`receipt.ticketLabels.vehicle_model`)
        ? `receipt.ticketLabels.vehicle_model`
        : `manageBooking.ticketLabels.vehicle_model`;
    },
    vehicleColourTranslation(): string {
      return this.$te(`receipt.ticketLabels.vehicle_colour`)
        ? `receipt.ticketLabels.vehicle_colour`
        : `manageBooking.ticketLabels.vehicle_colour`;
    },
    showFlightLookup(): boolean {
      return getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP) === true
        && getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP_PARKING) === true
        && getAppVariable('is_flight_lookup_enabled');
    },
    inboundDetailedFlight(): string {
      const { inbound_flight, inbound_airline, inbound_airport, inbound_terminal } = this.user;
      if (inbound_flight && inbound_airline) {
        let flightDetails = `${inbound_flight}, ${inbound_airline}`;
        if (inbound_airport) {
          flightDetails += `, ${inbound_airport}`;
        }
        if (inbound_terminal) {
          flightDetails += `, T${inbound_terminal}`;
        }
        return flightDetails;
      } else if (inbound_flight) {
        return inbound_flight;
      } else {
        return '-';
      }
    },
    outboundDetailedFlight(): string {
      const { outbound_flight, outbound_airline, outbound_airport, outbound_terminal } = this.user;
      if (outbound_flight && outbound_airline) {
        let flightDetails = `${outbound_flight}, ${outbound_airline}`;
        if (outbound_airport) {
          flightDetails += `, ${outbound_airport}`;
        }
        if (outbound_terminal) {
          flightDetails += `, T${outbound_terminal}`;
        }
        return flightDetails;
      } else if (outbound_flight) {
        return outbound_flight;
      } else {
        return '-';
      }
    },
  },

  methods: {
    isVisible(label: string, value: string | null): boolean {
      return this.isLabelSupportedAndExists(label) && !!value;
    },

    isLabelSupportedAndExists(label: string): boolean {
      return (
        this.$te(`receipt.ticketLabels.${label}`) ||
        this.$te(`manageBooking.ticketLabels.${label}`)
      );
    },
    returnTranslatedLabel(label: string): string {
      return this.$te(`receipt.ticketLabels.${label}`)
        ? `receipt.ticketLabels.${label}`
        : `manageBooking.ticketLabels.${label}`;
    },
  },
});
